import { getSharedProfileCookieName, encodeSharedProfile, decodeSharedProfile } from './sharedAuthProfile.js'
import { redirectToMeetingLogin } from './meetingLoginRedirect.js'

const COOKIE_SCOPE = 'Domain=meritco-services.com; Path=/; Secure; SameSite=Lax'
// Storage lifetime only; token validity is always decided by the backend.
const COOKIE_MAX_AGE = 30 * 24 * 60 * 60
const SHARED_ENVIRONMENTS = [
  {
    hosts: new Set(['forum.meritco-services.com', 'forum-live.meritco-services.com']),
    cookieName: 'forumToken_prod',
  },
  {
    hosts: new Set(['forum-qa.meritco-services.com', 'forum-live-qa.meritco-services.com']),
    cookieName: 'forumToken_qa',
  },
]

function getSharedEnvironment(location) {
  return location.protocol === 'https:'
    ? SHARED_ENVIRONMENTS.find(environment => environment.hosts.has(location.hostname))
    : undefined
}

export function createForumSession({ window: win, document: doc, storage, sessionStorage: tabStorage, onInvalidProfile = () => {} }) {
  const cookieName = getSharedEnvironment(win.location)?.cookieName
  const profileCookieName = getSharedProfileCookieName(cookieName)
  const shared = Boolean(cookieName)
  const listeners = new Set()
  let current
  let revision = 0
  let stopMonitoring
  let changeSource = 'external'
  let invalidProfileNotified = false
  const safeRead = key => { try { return storage.getItem(key) || '' } catch { return '' } }
  const mirror = token => {
    try { token ? storage.setItem('forumToken', token) : storage.removeItem('forumToken') } catch { /* Cookie remains authoritative. */ }
  }
  const readCookie = name => {
    const values = doc.cookie.split(';').map(part => part.trim()).filter(part => part.startsWith(`${name}=`))
    return values.length === 1 ? values[0].slice(name.length + 1) : ''
  }
  const writeProfile = value => {
    try {
      doc.cookie = `${profileCookieName}=${value}; ${COOKIE_SCOPE}; Max-Age=${value ? COOKIE_MAX_AGE : 0}`
      return readCookie(profileCookieName) === value
    } catch { return false }
  }
  const read = () => {
    if (!shared) return safeRead('forumToken')
    try {
      // Conflicting host/path cookies must not silently select another account.
      return normalizeToken(decodeURIComponent(readCookie(cookieName)))
    } catch { return '' }
  }
  function snapshot() {
    let token = read()
    let invalidProfile = false
    if (shared && token && !readProfile(token)) {
      // Check the captured token again before clearing. A late result for A
      // must never clear an already-published account B.
      const expectedToken = token
      if (read() === expectedToken && !readProfile(expectedToken)) {
        writeProfile('')
        try { doc.cookie = `${cookieName}=; ${COOKIE_SCOPE}; Max-Age=0` } catch { /* Deny locally even if cookies cannot be removed. */ }
        token = ''
        invalidProfile = true
      } else token = read()
    }
    if (current !== token) {
      const previous = current
      current = token
      revision += 1
      // Never migrate old localStorage or another environment's cookie.
      if (previous !== undefined || (shared && safeRead('forumToken') !== token)) {
        try {
          storage.removeItem('info')
          storage.removeItem('meetingFeedbackPending')
          tabStorage?.removeItem('meetingOperator')
        } catch { /* Storage may be disabled. */ }
      }
      mirror(token)
      const state = { token, revision }
      if (previous !== undefined) {
        for (const listener of listeners) {
          try { listener({ ...state, source: changeSource }) } catch { /* One view cannot prevent other views from syncing. */ }
        }
      }
    } else if (shared) mirror(token)
    if (invalidProfile && !invalidProfileNotified) {
      invalidProfileNotified = true
      onInvalidProfile()
    }
    return { token: current, revision }
  }
  const isCurrent = expected => {
    const state = snapshot()
    return Boolean(expected && state.token === expected.token && state.revision === expected.revision)
  }
  function readProfile(token) {
    try { return decodeSharedProfile(readCookie(profileCookieName), token) } catch { return null }
  }
  function getProfile() {
    if (!shared) return null
    const token = snapshot().token
    if (!token) return null
    return readProfile(token)
  }
  function publish(token, expected = snapshot(), loginInfo = null) {
    token = normalizeToken(token)
    if (!token || !isCurrent(expected)) return false
    if (shared) {
      // Leave room for cookie attributes; never truncate an authentication token.
      if (encodeURIComponent(token).length > 3500) return false
      // Reject incomplete login data before changing either cookie. A valid
      // profile is required for session restoration, never for authorization.
      const encodedProfile = encodeSharedProfile(loginInfo, token)
      if (!encodedProfile) return false
      const previousProfile = readCookie(profileCookieName)
      const restoreProfile = () => {
        if (read() === expected.token && readCookie(profileCookieName) === encodedProfile) writeProfile(previousProfile)
      }
      if (!writeProfile(encodedProfile)) { restoreProfile(); return false }
      if (read() !== expected.token) return false
      try { doc.cookie = `${cookieName}=${encodeURIComponent(token)}; ${COOKIE_SCOPE}; Max-Age=${COOKIE_MAX_AGE}` }
      catch { restoreProfile(); return false }
      if (read() !== token) { restoreProfile(); return false }
    } else mirror(token)
    changeSource = 'login'
    invalidProfileNotified = false
    try { return snapshot().token === token } finally { changeSource = 'external' }
  }
  function clear(expected = snapshot()) {
    if (!isCurrent(expected)) return false
    if (shared) {
      writeProfile('')
      try { doc.cookie = `${cookieName}=; ${COOKIE_SCOPE}; Max-Age=0` } catch { return false }
    } else mirror('')
    changeSource = 'logout'
    try { return snapshot().token === '' } finally { changeSource = 'external' }
  }
  function importLegacy(token) {
    // URL/window.name credentials are never a shared-session source (production or QA).
    return !shared && publish(token)
  }
  function start() {
    if (stopMonitoring) return stopMonitoring
    snapshot()
    const sync = () => snapshot()
    const visibleSync = () => { if (doc.visibilityState !== 'hidden') sync() }
    win.addEventListener('focus', sync)
    win.addEventListener('pageshow', sync)
    win.addEventListener('storage', sync)
    doc.addEventListener('visibilitychange', visibleSync)
    // Local cookie inspection only: no polling API calls or telemetry.
    const interval = win.setInterval(visibleSync, 5000)
    stopMonitoring = () => {
      win.clearInterval(interval)
      win.removeEventListener('focus', sync)
      win.removeEventListener('pageshow', sync)
      win.removeEventListener('storage', sync)
      doc.removeEventListener('visibilitychange', visibleSync)
      stopMonitoring = undefined
    }
    return stopMonitoring
  }
  return { shared, snapshot, isCurrent, publish, clear, importLegacy, start, getProfile,
    getToken: () => snapshot().token,
    subscribe: listener => { snapshot(); listeners.add(listener); return () => listeners.delete(listener) },
  }
}

function normalizeToken(token) {
  return typeof token === 'string' && token.length > 0 && token.length <= 2048 &&
    !/[\s\p{Cc}]/u.test(token) && !['undefined', 'null', 'ceshitoken'].includes(token)
    ? token : ''
}

export function getLoginToken(response) {
  const result = response?.result
  if (Number(response?.code) !== 200 || !result || result.registerToken ||
    (result.code !== undefined && Number(result.code) !== 200) ||
    [response.loginResult, result.loginResult].some(value => value !== undefined && Number(value) !== 1) ||
    [response.status, response.code, result.status, result.state, result.code].includes('NEED_REGISTER') ||
    Number(result.researchRole?.visit) === 1) return ''
  return normalizeToken(result.token)
}

let session
export const getForumSession = () => {
  if (!session) session = createForumSession({ window, document, storage: localStorage, sessionStorage, onInvalidProfile: redirectToMeetingLogin })
  return session
}
export const getForumToken = () => getForumSession().getToken()
export const getForumProfile = () => getForumSession().getProfile()
export const captureForumSession = () => getForumSession().snapshot()
export const isCurrentForumSession = snapshot => getForumSession().isCurrent(snapshot)
export const publishForumLogin = (response, expected) => getForumSession().publish(getLoginToken(response), expected, response?.result)
export const clearForumSession = expected => getForumSession().clear(expected)
export const importLegacyForumToken = token => getForumSession().importLegacy(token)
export const subscribeForumSession = listener => getForumSession().subscribe(listener)

export function staleForumSessionError() {
  const error = new Error('Login session changed; the previous response was discarded.')
  error.code = 'FORUM_SESSION_CHANGED'
  error.isStaleSession = true
  return error
}
